import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizationContext } from './orgContext';

type Slide = {
  id: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  bullets: string[];
  note?: string;
};

const slides: Slide[] = [
  {
    id: 'context-intro',
    eyebrow: 'Who We Are',
    title: 'Exam Masters is a tutoring and exam-preparation platform',
    subtitle: 'We help institutes and tutors deliver structured learning, guided practice, and measurable results in one system.',
    bullets: [
      'Built for structured tutoring and exam-preparation workflows',
      'Combines teaching content, practice, assignments, and testing operations',
      'Supports tutors, students, and admins with role-based access',
      'Designed to improve outcomes through consistency, analytics, and feedback loops',
    ],
    note: 'Use this slide first to set context before showing individual features.',
  },
  {
    id: 'overview',
    eyebrow: 'Tutor Overview',
    title: 'One operating system for tutors, classes, and exam preparation',
    subtitle: 'Show this first: how a tutor can run teaching, practice, and performance review from one platform.',
    bullets: [
      'Tutor creates courses, categories, and class structure before teaching begins',
      'Questions can be authored manually or generated with AI then approved by tutor',
      'Assignments, practice tests, and exam review happen in a single workflow',
      'Students practice independently while tutors monitor attempts and scores',
    ],
  },
  {
    id: 'user-onboarding',
    eyebrow: 'Tutor Setup',
    title: 'How a new user is added to the system',
    subtitle: 'This answers the practical onboarding question immediately.',
    bullets: [
      'Admin creates tutor account and assigns organization access',
      'Tutor profile is linked to classes/batches they manage',
      'Students are added to class rosters with batch-level grouping',
      'Role-based access keeps tutor, student, and admin actions separate',
      'On first login, tutor can immediately start course/category setup',
    ],
  },
  {
    id: 'ai-categories-questions',
    eyebrow: 'AI Authoring',
    title: 'How categories and questions are added through AI',
    subtitle: 'AI accelerates drafting; tutor remains the final authority before anything is published.',
    bullets: [
      'Tutor enters topic prompt, difficulty level, and question type requirements',
      'AI proposes category outlines and draft questions with options/explanations',
      'Tutor edits wording, validates accuracy, and removes any weak items',
      'Approved questions are saved into the question bank for assignments/tests',
      'This reduces content creation time while preserving quality control',
    ],
    note: 'AI suggests, tutor approves.',
  },
  {
    id: 'assignment-flow',
    eyebrow: 'Assignments Flow',
    title: 'Assignment flow from tutor to student',
    subtitle: 'This is the daily usage loop and should be shown clearly in sequence.',
    bullets: [
      'Tutor selects class/batch and picks questions from the bank',
      'Sets due date, attempt rules, and optional time limit',
      'Students receive assignment in their practice area dashboard',
      'System tracks pending, started, submitted, and completed states',
      'Tutor reviews completion and performance summary per student',
    ],
  },
  {
    id: 'exam-review',
    eyebrow: 'Exam Review',
    title: 'How exam review is done after tests',
    subtitle: 'Position this as a feedback engine, not only a score report.',
    bullets: [
      'After exam, tutor sees class-level and student-level score breakdowns',
      'Question-wise analytics show common mistakes and weak topics',
      'Tutor can review incorrect responses and discuss better solving methods',
      'Reassignment can be created for weak chapters in one follow-up cycle',
      'Trend view helps compare performance across multiple tests',
    ],
  },
  {
    id: 'practice-ai-assistant',
    eyebrow: 'Practice + AI Assistant',
    title: 'Practice area and AI assistant in the same experience',
    subtitle: 'This is a strong differentiator: students practice and get guided support without leaving the flow.',
    bullets: [
      'Student attempts a question directly in the practice interface',
      'AI assistant provides hints, step guidance, and concept nudges on demand',
      'Tutor can configure boundaries so AI supports learning, not answer leakage',
      'Attempt history and AI interaction context can inform tutor remediation',
      'Students stay engaged because help is contextual to the current question',
    ],
  },
  {
    id: 'category-details-learning',
    eyebrow: 'Category Learning Page',
    title: 'Category details page for concepts, tips, and examples',
    subtitle: 'This slide shows how students learn chapter content before or during practice.',
    bullets: [
      'At /em/course-details/1, each category is presented with learning-focused details',
      'Students can read concept notes and tutor guidance for the topic',
      'Tips section helps with shortcuts, common mistakes, and exam strategy',
      'Examples section gives worked patterns before students attempt questions',
      'Tutor can align assignments with the same category flow for consistency',
    ],
    note: 'This is useful to show learning + practice in one connected journey.',
  },
  {
    id: 'monetization',
    eyebrow: 'Revenue Opportunities',
    title: 'Where money can be made',
    subtitle: 'You can present this as direct and indirect revenue streams for tutors and institutes.',
    bullets: [
      'Paid course enrollments (monthly/term-based plans)',
      'Premium mock test packs and homework bundles',
      'Institute subscriptions for multi-teacher usage',
      'Performance mentoring upsells based on analytics insights',
      'Content licensing/white-labeling for partner institutes',
    ],
    note: 'Revenue mix can combine recurring subscriptions + one-time assessment packs.',
  },
  {
    id: 'presentation-flow',
    eyebrow: 'Presentation Flow',
    title: 'How to present this in 5-7 minutes',
    subtitle: 'Tutor-first narration sequence for your friend or institute stakeholder.',
    bullets: [
      'Slide 1: Platform overview for tutors',
      'Slide 2: How users/tutors/students are added',
      'Slide 3: AI category and question creation',
      'Slide 4: Assignment delivery and tracking flow',
      'Slide 5: Exam review and intervention planning',
      'Slide 6: Practice + AI assistant experience',
      'Slide 7: Monetization and growth model',
    ],
  },
];

const WhatWeDo: React.FC = () => {
  const navigate = useNavigate();
  const organizationContext = getOrganizationContext();
  const orgName = organizationContext?.organizationName || organizationContext?.organizationSlug || 'Exam Masters';
  const [activeIndex, setActiveIndex] = useState(0);

  const activeSlide = slides[activeIndex];

  const goPrev = () => setActiveIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  const goNext = () => setActiveIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));

  return (
    <div className="bg-gradient-to-br from-app-page-from to-app-page-to min-h-screen">
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">What We Do</span>
        </div>
      </div>

      <div className="px-3 py-3 md:px-4 lg:px-6">
        <div className="mx-auto max-w-6xl rounded-xl border border-white/40 bg-white shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-4 md:px-6 md:py-5">
            <p className="text-xs font-semibold tracking-wider uppercase text-white/85">Overview</p>
            <h1 className="mt-1 text-xl md:text-2xl font-bold leading-tight">What We Do</h1>
            <p className="mt-1 text-xs md:text-sm text-white/90 max-w-3xl">
              Use this page as a tutor-focused walkthrough: onboarding, AI content creation, assignments, review, and monetization.
            </p>
          </div>

          <div className="p-4 md:p-6">
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-cyan-50 p-4 md:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">{activeSlide.eyebrow}</p>
                  <h2 className="mt-1 text-xl md:text-2xl font-bold text-slate-900 leading-tight">{activeSlide.title}</h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600 max-w-3xl">{activeSlide.subtitle}</p>
                </div>
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs md:text-sm font-semibold text-cyan-800">
                  {orgName}
                </div>
              </div>

              <div className="mt-5">
                <div className="rounded-xl border border-slate-200 bg-white p-4 min-h-[340px] md:min-h-[360px] lg:h-[380px] overflow-y-auto">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Key Points</p>
                  <ul className="mt-3 space-y-2 text-sm md:text-base text-slate-700">
                    {activeSlide.bullets.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-600" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {activeSlide.note && (
                    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                      {activeSlide.note}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Slide {activeIndex + 1} / {slides.length}</p>
                <div className="mt-2 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={goPrev}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-700"
                  >
                    Next
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  {slides.map((slide, index) => (
                    <button
                      key={slide.id}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      className={`h-2.5 w-2.5 rounded-full ${index === activeIndex ? 'bg-cyan-600' : 'bg-slate-300'}`}
                      aria-label={`Go to slide ${index + 1}`}
                      title={slide.title}
                    />
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatWeDo;
